import React, { Suspense, lazy, Component } from "react";
import { Button, Flex, toast } from "@appsmith/ads";
import { carbonIntegrationsAdapter } from "./RagUtils";
import { get } from "lodash";
import ErrorBoundary from "components/editorComponents/ErrorBoundry";
import AppsmithLogo from "assets/images/appsmith_logo_square.png";
import { getDatasource } from "ce/selectors/entitiesSelector";
import { fetchAccessToken, fetchIntegrations } from "./RagApiRequests";
import { connect } from "react-redux";
import { getAppsmithConfigs } from "ee/configs";
import { TEMP_DATASOURCE_ID } from "constants/Datasource";
import type { AppState } from "ee/reducers";
import type { ApiResponseError } from "api/types";
import { type EmbeddingGenerators, type Integration } from "carbon-connect";
import "./carbon-modal.css";

const CarbonConnect = lazy(async () => import("./CarbonConnect"));
const { carbonApiBasePath } = getAppsmithConfigs();

interface CarbonButtonProps {
  datasourceId: string;
  workspaceId: string;
  datasourceIntegrations?: Integration[];
  onModalStateChange?: (value: boolean) => void;
}

const loadingError = <div>Failed to load CarbonConnect</div>;

const fallbackButton = (
  <Button isLoading size="md" startIcon="add-more">
    Add data
  </Button>
);

interface CarbonButtonState {
  isIntegrationsLoading: boolean;
  isCarbonModalOpen: boolean;
  carbonIntegrations: Integration[];
  embeddingModel?: keyof typeof EmbeddingGenerators;
}

class _CarbonButton extends Component<CarbonButtonProps, CarbonButtonState> {
  state = {
    isIntegrationsLoading: false,
    isCarbonModalOpen: false,
    carbonIntegrations: [],
    embeddingModel: undefined,
  };

  private onCarbonModalOpen = async () => {
    const { datasourceId, onModalStateChange, workspaceId } = this.props;

    if (datasourceId === TEMP_DATASOURCE_ID) {
      toast.show("Please, save the data source before importing data points.", {
        kind: "error",
      });

      return;
    }

    // If integrations has already been loaded, then just open CarbonModal
    if (this.state.carbonIntegrations.length !== 0) {
      this.setState({
        isCarbonModalOpen: true,
      });

      onModalStateChange && onModalStateChange(true);

      return;
    }

    this.setState({
      isIntegrationsLoading: true,
    });

    try {
      const { embeddingModel, integrations } = await fetchIntegrations(
        datasourceId,
        workspaceId,
      );

      this.setState({
        isIntegrationsLoading: false,
        carbonIntegrations: integrations,
        embeddingModel,
        isCarbonModalOpen: true,
      });

      onModalStateChange && onModalStateChange(true);
    } catch (error) {
      toast.show((error as ApiResponseError).message, { kind: "error" });
      this.setState({
        isIntegrationsLoading: false,
      });
    }
  };

  render() {
    const {
      datasourceId,
      datasourceIntegrations,
      onModalStateChange,
      workspaceId,
    } = this.props;

    return (
      <ErrorBoundary fallback={loadingError}>
        <Suspense fallback={fallbackButton}>
          <Flex alignItems="center" gap="spaces-3">
            <Button
              isLoading={this.state.isIntegrationsLoading}
              kind="secondary"
              onClick={this.onCarbonModalOpen}
              size="md"
            >
              Import data points
            </Button>
            Upload files, connect your Google Drive, Jira etc.
          </Flex>
          {this.state.isCarbonModalOpen && (
            <CarbonConnect
              apiURL={carbonApiBasePath}
              backButtonText="Close"
              brandIcon={AppsmithLogo}
              embeddingModel={this.state.embeddingModel}
              enabledIntegrations={carbonIntegrationsAdapter(
                datasourceIntegrations,
                this.state.carbonIntegrations,
              )}
              open={this.state.isCarbonModalOpen}
              orgName="Appsmith"
              setOpen={(isOpen) => {
                this.setState({ isCarbonModalOpen: isOpen as boolean });

                if (onModalStateChange) {
                  onModalStateChange(isOpen as boolean);
                }
              }}
              theme="light"
              tokenFetcher={async () =>
                fetchAccessToken(datasourceId, workspaceId)
              }
            />
          )}
        </Suspense>
      </ErrorBoundary>
    );
  }
}

export const CarbonButton = connect(
  (state: AppState, ownProps: CarbonButtonProps) => {
    const datasource = getDatasource(state, ownProps.datasourceId);

    const datasourceIntegrations = get(
      datasource,
      `datasourceStorages.${state.environments.currentEnvironmentDetails.id}.datasourceConfiguration.properties[1].value`,
    ) as unknown as Integration[];

    return { datasourceIntegrations };
  },
)(_CarbonButton);
